import React, { useCallback, useRef } from 'react';
import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { DashboardQueryDrilldownDialog } from 'components/dialogs/QueryDrilldown/DashboardQueryDrilldownDialog';
import { Query } from 'system/query/Query';
import WidgetAlert, { WidgetErrorType } from 'components/widgetParts/WidgetAlert';
import WidgetLayout from 'components/widgetParts/WidgetLayout';
import WidgetTitle from 'components/widgetParts/WidgetTitle';
import { defaultSettings, ForceGraphSettingsStateType } from './ForceGraphSettings';
import ForceGraph, { Cluster, ForceGraphResponse, SimulationLink, SimulationNode } from './ForceGraph';
import withQueryWidget, { WithQueryWidgetProps } from 'components/widgetParts/queryWidgetComposition/withQueryWidget';
import { getSingleOriginalRequestByName, getSingleWidgetResponseByName } from 'system/widget/Widget';

function ForceGraphContent(props: WithQueryWidgetProps): JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const apiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'force-graph');
  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'force-graph');
  const responseBody = apiResponseInfo?.responseBody || {};

  const [selectedCluster, setSelectedCluster] = React.useState<Cluster | null>(null);

  const linkColorCache = useRef<Record<string, string | CanvasGradient | CanvasPattern>>({});

  const currentState: ForceGraphSettingsStateType = {
    ...defaultSettings,
    ...props.widget?.data?.settings
  };

  const chartColors = theme.colors.namedColors.chartColorArray;

  const sentimentalColors = [
    theme.colors.namedColors.neutralLight,
    theme.colors.namedColors.positive,
    theme.colors.namedColors.negative
  ];

  const linkColorFunction = useCallback((link: SimulationLink, canvas: CanvasRenderingContext2D): string | CanvasGradient | CanvasPattern => {
    let cacheKey = `${currentState.colorBy}-${link.source.c}-${link.target.c}`;
    let sourceColor = chartColors[link.source.c % chartColors.length];
    let targetColor = chartColors[link.target.c % chartColors.length];
    if (currentState.colorBy === "sentiment") {
      cacheKey = `${currentState.colorBy}-${link.source.s-1}-${link.target.s-1}`;
      sourceColor = sentimentalColors[link.source.s-1];
      targetColor = sentimentalColors[link.target.s-1];
    }

    // Creating a linear gradient is very expensive to do every frame.  Caching and reusing them
    // greatly improves performance.
    let linearGradient = linkColorCache.current[cacheKey];
    if (!linearGradient) {
      linearGradient = canvas.createLinearGradient(link.source.x, link.source.y, link.target.x, link.target.y);
      linearGradient.addColorStop(0.0, sourceColor);
      linearGradient.addColorStop(1.0, targetColor);
      linkColorCache.current[cacheKey] = linearGradient;
    }

    return linearGradient;
  }, []);

  const nodeColorFunction = useCallback((node: SimulationNode, canvas: CanvasRenderingContext2D): string | CanvasGradient | CanvasPattern => {
    if (currentState.colorBy === "sentiment") {
      return sentimentalColors[node.s-1];
    }
    return chartColors[node.c % chartColors.length];
  }, []);

  const forceGraphResponse = responseBody as ForceGraphResponse;
  return (
    <>
      {forceGraphResponse?.nodes?.length !== 0 &&
        <WidgetLayout
          header={
            <WidgetTitle
              alignment={currentState.widgetTitleAlignment}
              customTitle={currentState.customWidgetTitle}
              defaultTitle={i18n.t("widget-types.force-graph.label")}
              settingsTitle={currentState.widgetTitle}
              style={{ pb: 2 }}
              widgetQueryTitle={apiResponseInfo?.queryDisplayTitle} />
          }
        >
          {responseBody &&
            <>
            <ForceGraph
              data={forceGraphResponse}
              displayLinks={currentState.displayLinks}
              labelDisplay={currentState.labelDisplay}
              linkColorFunction={linkColorFunction}
              nodeColorFunction={nodeColorFunction}
              onClick={(node, cluster) => {
                setSelectedCluster(cluster);
              }}
              width={props.contentRef.current.clientWidth}
              height={props.contentRef.current.clientHeight}
            />
            {selectedCluster &&
              <DashboardQueryDrilldownDialog
                title={selectedCluster.label}
                queryInfo={props.queryInfo}
                onClose={() => setSelectedCluster(null)}
                activeQueryIds={apiResponseInfo?.originatingRequest?.activeQueryIds}
                drilldownQuery={new Query({query: selectedCluster.query})}
                widgetRequestInfo={originalRequest} />
            }
            </>
          }
        </WidgetLayout>
      }
      {forceGraphResponse?.nodes?.length === 0 &&
        <WidgetAlert type={WidgetErrorType.NO_DATA} />
      }
    </>
  );
}

export default withQueryWidget(ForceGraphContent, {
  hasSettingsDialog: true,
  hasQueryDialog: true
});

